import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { videoArticles } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import { Helmet } from '@/components/base/SEOHead';
import VideoCard from '@/components/base/VideoCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => {
    return videoArticles.find((v) => v.id === Number(id));
  }, [id]);

  const category = useMemo(() => {
    if (!article) return null;
    return categories.find((c) => c.slug === article.category);
  }, [article]);

  const relatedVideos = useMemo(() => {
    if (!article) return [];
    return videoArticles
      .filter((v) => v.id !== article.id && v.category === article.category)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground-950 mb-2">视频不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.thumbnail,
    url: `https://fulivideo.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '福利视频大全',
    },
    articleBody: article.content.replace(/<[^>]*>/g, ''),
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fulivideo.com' },
      ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `https://fulivideo.com/category/${category.slug}` }] : []),
      { '@type': 'ListItem', position: category ? 3 : 2, name: article.title },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{article.title} - 福利视频大全</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={article.tags.join(',')} />
        <link rel="canonical" href={`https://fulivideo.com/article/${article.id}`} />
        <meta property="og:title" content={`${article.title} - 福利视频大全`} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={`https://fulivideo.com/article/${article.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={article.thumbnail} />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:author" content={article.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={article.thumbnail} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main>
        <article>
          <header className="py-10 md:py-14 bg-background-100">
            <div className="mx-auto max-w-[960px] px-4 md:px-6">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                {category && (
                  <>
                    <Link to={`/category/${category.slug}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                      {category.name}
                    </Link>
                    <i className="ri-arrow-right-s-line"></i>
                  </>
                )}
                <span className="text-foreground-950 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 md:gap-5 text-xs text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次观看
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.duration}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </header>

          <div className="py-8 md:py-12 bg-background-50">
            <div className="mx-auto max-w-[960px] px-4 md:px-6">
              <div className="aspect-video rounded-lg overflow-hidden mb-8">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`${article.title} - 福利视频大全`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button className="flex items-center gap-1.5 text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors text-sm whitespace-nowrap">
                      <i className="ri-thumb-up-line"></i>
                      赞 {article.likes > 10000 ? `${(article.likes / 10000).toFixed(1)}万` : article.likes}
                    </button>
                    <button className="flex items-center gap-1.5 text-foreground-500 hover:text-primary-500 cursor-pointer transition-colors text-sm whitespace-nowrap">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="mx-auto max-w-[1280px] px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}